/**
 * Pricing Adjustment Step
 *
 * Component for adjusting pricing with line item breakdown and deviation warnings.
 * Shows warning if pricing deviates >10% from standard rates.
 *
 * DDD Compliance:
 * - Purely presentational component
 * - Calculates deviation percentage (simple math, not business logic)
 * - Parent handles data persistence
 */

interface PricingData {
  base: number;
  deliveryFee?: number;
  disposalFee?: number;
  additionalFees?: number;
  discount?: number;
}

interface PricingServiceData {
  serviceType: string;
  containerSize: string;
  materialType: string;
  [key: string]: unknown;
}

interface PricingAdjustmentStepProps {
  data: PricingData | null;
  onDataChange: (data: PricingData) => void;
  serviceData: PricingServiceData | null;
}

/**
 * Standard pricing rates based on service type and container size
 * These would typically come from a backend pricing service
 */
const STANDARD_RATES = {
  delivery: {
    '10Y': { base: 150, delivery: 50 },
    '20Y': { base: 200, delivery: 75 },
    '30Y': { base: 250, delivery: 100 },
    '40Y': { base: 300, delivery: 125 },
  },
  pickup: {
    '10Y': { base: 150, delivery: 50 },
    '20Y': { base: 200, delivery: 75 },
    '30Y': { base: 250, delivery: 100 },
    '40Y': { base: 300, delivery: 125 },
  },
  swap: {
    '10Y': { base: 200, delivery: 75 },
    '20Y': { base: 275, delivery: 100 },
    '30Y': { base: 350, delivery: 125 },
    '40Y': { base: 425, delivery: 150 },
  },
  permanent_placement: {
    '10Y': { base: 125, delivery: 40 },
    '20Y': { base: 175, delivery: 60 },
    '30Y': { base: 225, delivery: 80 },
    '40Y': { base: 275, delivery: 100 },
  },
};

const DISPOSAL_RATES = {
  mixed: 85,
  concrete: 45,
  metal: 25,
  wood: 65,
  green: 55,
};

export function PricingAdjustmentStep({
  data,
  onDataChange,
  serviceData,
}: PricingAdjustmentStepProps) {
  // Calculate standard pricing based on service details
  const standardPricing = calculateStandardPricing(serviceData);
  const currentTotal = calculateTotal(data);
  const standardTotal = calculateTotal(standardPricing);
  const deviationPercent = calculateDeviation(currentTotal, standardTotal);
  const showWarning = Math.abs(deviationPercent) > 10;

  // Initialize with standard pricing if no data exists
  if (!data && standardPricing) {
    onDataChange(standardPricing);
  }

  const handleFieldChange = (field: keyof PricingData, value: string) => {
    const numValue = value === '' ? 0 : parseFloat(value);
    if (isNaN(numValue)) return;

    onDataChange({
      ...(data || { base: 0 }),
      [field]: numValue,
    });
  };

  const resetToStandard = () => {
    if (standardPricing) {
      onDataChange(standardPricing);
    }
  };

  return (
    <div className="space-y-6">
      {/* Standard Pricing Reference */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">
              Standard Pricing Reference
            </h4>
            <div className="space-y-1 text-sm text-blue-800">
              <div className="flex justify-between">
                <span>Base Rate ({serviceData?.containerSize}):</span>
                <span className="font-medium">${standardPricing?.base.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span>Delivery Fee:</span>
                <span className="font-medium">
                  ${standardPricing?.deliveryFee?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Disposal Fee ({serviceData?.materialType}):</span>
                <span className="font-medium">
                  ${standardPricing?.disposalFee?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="flex justify-between pt-1 border-t border-blue-300">
                <span className="font-semibold">Standard Total:</span>
                <span className="font-semibold">${standardTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={resetToStandard}
            className="ml-4 text-xs text-blue-700 hover:text-blue-900 underline"
          >
            Reset to Standard
          </button>
        </div>
      </div>

      {/* Editable Pricing Fields */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-neutral-900">Adjust Pricing</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Base Price */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Base Rate
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-neutral-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={data?.base || 0}
                onChange={(e) => handleFieldChange('base', e.target.value)}
                className="input-field pl-7"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Delivery Fee */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Delivery Fee
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-neutral-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={data?.deliveryFee || 0}
                onChange={(e) => handleFieldChange('deliveryFee', e.target.value)}
                className="input-field pl-7"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Disposal Fee */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Disposal Fee
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-neutral-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={data?.disposalFee || 0}
                onChange={(e) => handleFieldChange('disposalFee', e.target.value)}
                className="input-field pl-7"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Additional Fees */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Additional Fees
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-neutral-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={data?.additionalFees || 0}
                onChange={(e) => handleFieldChange('additionalFees', e.target.value)}
                className="input-field pl-7"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Discount */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Discount
            </label>
            <div className="relative max-w-xs">
              <span className="absolute left-3 top-2.5 text-neutral-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={data?.discount || 0}
                onChange={(e) => handleFieldChange('discount', e.target.value)}
                className="input-field pl-7"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Total and Deviation Warning */}
      <div className="border-t border-neutral-200 pt-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-lg font-semibold text-neutral-900">Total Price</span>
          <span className="text-2xl font-bold text-neutral-900">
            ${currentTotal.toFixed(2)}
          </span>
        </div>

        {/* Deviation Indicator */}
        <div className="flex justify-between items-center text-sm">
          <span className="text-neutral-600">Deviation from Standard</span>
          <span
            className={`font-medium ${
              Math.abs(deviationPercent) < 5
                ? 'text-green-600'
                : showWarning
                  ? 'text-red-600'
                  : 'text-yellow-600'
            }`}
          >
            {deviationPercent > 0 ? '+' : ''}
            {deviationPercent.toFixed(1)}%
          </span>
        </div>

        {/* Warning Message */}
        {showWarning && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div className="flex-1">
                <h5 className="text-sm font-semibold text-yellow-900 mb-1">
                  Manager Approval Required
                </h5>
                <p className="text-sm text-yellow-800">
                  This pricing deviates more than 10% from standard rates and will require
                  manager approval before the job can be dispatched.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pricing Notes */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Pricing Notes (Optional)
        </label>
        <textarea
          placeholder="Add any notes about pricing adjustments..."
          className="input-field"
          rows={3}
        />
        <p className="text-xs text-neutral-500 mt-1">
          Explain why pricing was adjusted from standard rates
        </p>
      </div>
    </div>
  );
}

// Helper Functions

function calculateStandardPricing(serviceData: PricingServiceData | null): PricingData | null {
  if (!serviceData) return null;

  const serviceType = serviceData.serviceType as keyof typeof STANDARD_RATES;
  const containerSize = serviceData.containerSize as keyof typeof STANDARD_RATES[typeof serviceType];
  const materialType = serviceData.materialType.toLowerCase();

  const rates = STANDARD_RATES[serviceType]?.[containerSize];
  if (!rates) return null;

  // Find disposal rate by partial match
  const disposalKey = Object.keys(DISPOSAL_RATES).find((key) =>
    materialType.includes(key)
  ) as keyof typeof DISPOSAL_RATES;
  const disposalFee = disposalKey ? DISPOSAL_RATES[disposalKey] : 75;

  return {
    base: rates.base,
    deliveryFee: rates.delivery,
    disposalFee: disposalFee,
  };
}

function calculateTotal(pricing: PricingData | null): number {
  if (!pricing) return 0;

  return (
    (pricing.base || 0) +
    (pricing.deliveryFee || 0) +
    (pricing.disposalFee || 0) +
    (pricing.additionalFees || 0) -
    (pricing.discount || 0)
  );
}

function calculateDeviation(current: number, standard: number): number {
  if (standard === 0) return 0;
  return ((current - standard) / standard) * 100;
}
